import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel} from '@Components/panel';
import {OFFSET} from '@PosterWhiteboard/page/add-item.class';
import {MultipleItemsSelectedNotificationTag} from '@Components/poster-editor/components/multiple-items-selected-notification-tag';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import type {BottomBarItemProps} from '@Components/contextual-menu/components/bottom-bar-item';
import PosterEditingItemPositionPanel from '@Panels/poster-editing-item-position-panel';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import PosterEditingItemSymmetryPanel from '@Panels/poster-editing-item-symmetry-panel/poster-editing-item-symmetry-panel';
import type {Point} from '@Utils/math.util';
import type * as Fabric from '@postermywall/fabricjs-2';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {useAppSelector} from '@/hooks';
import styles from './poster-editor-more-options-panel.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';

interface PosterEditorMoreOptionsPanelProps {
  panelId: string;
}

enum PosterItemMoreOptions {
  COPY = 'copy',
  PASTE = 'paste',
  MULTISELECT = 'multiselect',
  CLEARSELECTION = 'clearselection',
  POSITION = 'position',
  EDIT = 'edit',
  DUPLICATE = 'duplicate',
  SPELLCHECK = 'spellcheck',
  LOCK = 'lock',
  DELETE = 'delete',
  SYMMETRY = 'symmetry',
}

enum PANEL_TO_SHOW {
  DEFAULT = 'DEFAULT',
  POSITION = 'POSITION',
  SYMMETRY = 'SYMMETRY',
}

export default function PosterEditorMoreOptionsPanel({...props}: PosterEditorMoreOptionsPanelProps): ReactElement | null {
  const [panelToShow, setPanelToShow] = useState(PANEL_TO_SHOW.DEFAULT);
  const onClose = useDefaultPanelClose(props.panelId);
  const poster = window.posterEditor?.whiteboard!;
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const activeItemLockMovement = useGetKeyFromActiveItem('lockMovementX');

  const totalActiveItems = useAppSelector((state) => {
    if (!currentPage) {
      return 0;
    }

    return state.posterEditor.activeItemIds[currentPage.hashedID]?.length ?? 0;
  });

  useEffect(() => {
    return () => {
      setTimeout((): void => {
        poster.clearSelection();
        updateOptionsPanelHeight(props.panelId, '86px');
      }, 250);
    };
  }, []);

  useEffect((): void => {
    let height = '86px';
    switch (panelToShow) {
      case PANEL_TO_SHOW.POSITION:
        height = '404px';
        break;
      case PANEL_TO_SHOW.SYMMETRY:
        if (totalActiveItems > 2) {
          height = '426px';
        } else {
          height = '292px';
        }
        break;
      case PANEL_TO_SHOW.DEFAULT:
      default:
    }

    updateOptionsPanelHeight(props.panelId, height);
  }, [panelToShow, totalActiveItems]);

  const onClosePanel = (): void => {
    if (totalActiveItems > 1) {
      poster.clearSelection();
    }
    onClose();
  };

  const getItems = (): BottomBarItemProps[] => {
    if (totalActiveItems > 1) {
      return getGroupOptions();
    }
    if (totalActiveItems === 0) {
      return getCanvasOptions();
    }
    return getSingleItemOptions();
  };

  const getCanvasOptions = (): BottomBarItemProps[] => {
    return [
      {
        id: PosterItemMoreOptions.PASTE,
        icon: 'icon-paste',
        text: window.i18next.t('pmwjs_paste'),
      },
      {
        id: PosterItemMoreOptions.MULTISELECT,
        icon: 'icon-select-all',
        text: window.i18next.t('pmwjs_multi_select'),
        tag: <MultipleItemsSelectedNotificationTag />,
      },
    ];
  };

  const getSingleItemOptions = (): BottomBarItemProps[] => {
    return [
      {
        id: PosterItemMoreOptions.DUPLICATE,
        icon: 'icon-duplicate',
        text: window.i18next.t('pmwjs_duplicate'),
      },
      {
        id: PosterItemMoreOptions.POSITION,
        icon: 'icon-prints-stack',
        text: window.i18next.t('pmwjs_position'),
      },
      {
        id: PosterItemMoreOptions.MULTISELECT,
        icon: 'icon-select-all',
        text: window.i18next.t('pmwjs_multi_select'),
        tag: <MultipleItemsSelectedNotificationTag />,
      },
      {
        id: PosterItemMoreOptions.CLEARSELECTION,
        icon: 'icon-clear-selection',
        text: window.i18next.t('pmwjs_clear_selection'),
      },
      {
        id: PosterItemMoreOptions.COPY,
        icon: 'icon-copy',
        text: window.i18next.t('pmwjs_copy'),
      },
      {
        id: PosterItemMoreOptions.PASTE,
        icon: 'icon-paste',
        text: window.i18next.t('pmwjs_paste'),
      },
      {
        id: PosterItemMoreOptions.EDIT,
        icon: 'icon-pencil',
        text: window.i18next.t('pmwjs_edit'),
        isHidden: !(activeItem?.isText() || activeSlide?.isTextSlide()),
      },
      // {
      //   id: PosterItemMoreOptions.SPELLCHECK,
      //   icon: 'icon-spellcheck',
      //   text: window.i18next.t('pmwjs_spellcheck'),
      //   // iconClasses: 'color-danger-100',
      // },
      {
        id: PosterItemMoreOptions.LOCK,
        icon: activeItem?.fabricObject.lockMovementX ? 'icon-lock' : 'icon-unlock',
        text: activeItem?.fabricObject.lockMovementX ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        // iconClasses: 'color-danger-100',
      },
      {
        id: PosterItemMoreOptions.DELETE,
        icon: 'icon-delete',
        text: window.i18next.t('pmwjs_delete'),
        iconClasses: 'color-danger-100',
      },
    ];
  };

  const getGroupOptions = (): BottomBarItemProps[] => {
    return [
      {
        id: PosterItemMoreOptions.DUPLICATE,
        icon: 'icon-duplicate',
        text: window.i18next.t('pmwjs_duplicate'),
      },
      {
        id: PosterItemMoreOptions.POSITION,
        icon: 'icon-prints-stack',
        text: window.i18next.t('pmwjs_position'),
      },
      {
        id: PosterItemMoreOptions.SYMMETRY,
        icon: 'icon-center-align-shapes',
        text: window.i18next.t('pmwjs_symmetry'),
      },
      {
        id: PosterItemMoreOptions.MULTISELECT,
        icon: 'icon-select-all',
        text: window.i18next.t('pmwjs_multi_select'),
        tag: <MultipleItemsSelectedNotificationTag />,
      },
      {
        id: PosterItemMoreOptions.CLEARSELECTION,
        icon: 'icon-clear-selection',
        text: window.i18next.t('pmwjs_clear_selection'),
      },
      {
        id: PosterItemMoreOptions.COPY,
        icon: 'icon-copy',
        text: window.i18next.t('pmwjs_copy'),
      },
      {
        id: PosterItemMoreOptions.PASTE,
        icon: 'icon-paste',
        text: window.i18next.t('pmwjs_paste'),
      },
      {
        id: PosterItemMoreOptions.DELETE,
        icon: 'icon-delete',
        text: window.i18next.t('pmwjs_delete'),
        iconClasses: 'color-danger-100',
      },
    ];
  };

  const getContent = (): ReactElement => {
    return (
      <>
        <Icon className={styles.closeIcon} onClick={onClosePanel} icon="icon-close" shape={IconShape.CIRCLE} size={IconSize.SIZE_ICON_16} type={IconType.SHADOW} />
        <BottomBar isForEditorBottomBar={false} id="poster-item-more-options" className={`${styles.bottomBar} spacing-p-t-1`} onClick={onClickHandler} items={getItems()} />
      </>
    );
  };

  const onClickHandler = (id: PosterItemMoreOptions): void => {
    switch (id) {
      case PosterItemMoreOptions.COPY:
        onCopySelection();
        break;
      case PosterItemMoreOptions.PASTE:
        void onPaste();
        break;
      case PosterItemMoreOptions.MULTISELECT:
        onMultiSelectClicked();
        break;
      case PosterItemMoreOptions.CLEARSELECTION:
        onClearSelection();
        break;
      case PosterItemMoreOptions.POSITION:
        setPanelToShow(PANEL_TO_SHOW.POSITION);
        break;
      case PosterItemMoreOptions.SYMMETRY:
        setPanelToShow(PANEL_TO_SHOW.SYMMETRY);
        break;
      case PosterItemMoreOptions.EDIT:
        onEdit();
        break;
      case PosterItemMoreOptions.DUPLICATE:
        onDuplicate();
        break;
      // case PosterItemMoreOptions.SPELLCHECK:
      //   onForwardControlClicked();
      //   break;
      case PosterItemMoreOptions.LOCK:
        onLockItem();
        break;
      case PosterItemMoreOptions.DELETE:
        onDeleteSelection();
        break;
      default:
        throw new Error('unhandled poster item control option!');
    }
  };

  const onCopySelection = (): void => {
    if (currentPage) {
      currentPage.clipboard.copySelection();
      showMessageGrowl({
        text: window.i18next.t('pmwjs_copied'),
        icon: 'icon-copy',
        type: GROWL_TYPE.NEUTRAL,
        interval: 2000,
        showCloseIcon: false,
      });
    }
  };

  const onPaste = async (e?: React.MouseEvent<HTMLElement>): Promise<void> => {
    if (currentPage) {
      const canvas = currentPage.fabricCanvas as Fabric.Canvas;
      const activeObject = canvas.getActiveObject();
      let pastedItemCoords: Point;
      if (activeObject) {
        pastedItemCoords = {
          x: activeObject.left + OFFSET,
          y: activeObject.top + OFFSET,
        };
      } else {
        pastedItemCoords = {
          x: poster.width / 2,
          y: poster.height / 2,
        };
      }
      await currentPage.clipboard.pasteItems(e, {
        isPasteFromRightClick: true,
        eventCanvasOffset: {left: pastedItemCoords.x, top: pastedItemCoords.y},
      });
      showMessageGrowl({
        text: window.i18next.t('pmwjs_pasted'),
        icon: 'icon-poster-print',
        type: GROWL_TYPE.NEUTRAL,
        interval: 2000,
        showCloseIcon: false,
      });
    }
  };

  const onMultiSelectClicked = (): void => {
    poster.itemsMultiSelect.updateFromObject({
      enabled: !poster.itemsMultiSelect.enabled,
    });
  };

  const onClearSelection = (): void => {
    poster.clearSelection();
  };

  const onDuplicate = (): void => {
    currentPage?.activeSelection?.onDuplicate();
  };

  const onLockItem = (): void => {
    void activeItem?.updateLock(!activeItemLockMovement);
  };

  const onEdit = (): void => {
    if (activeItem?.isText() || activeItem?.isSlideshow()) {
      // @ts-ignore
      activeItem.enterEditMode();
    }
  };

  const onDeleteSelection = (): void => {
    currentPage?.activeSelection?.deleteSelectedItems();
  };

  const getPositionPanel = (): ReactElement => {
    return <PosterEditingItemPositionPanel onClose={onClose} showBackButton onBackButton={onBack} panelId={props.panelId} showPositionOptions />;
  };

  const getSymmetryPanel = (): ReactElement => {
    return <PosterEditingItemSymmetryPanel onClose={onClose} showBackButton onBackButton={onBack} panelId={props.panelId} />;
  };

  const getPanel = (): ReactElement | null => {
    switch (panelToShow) {
      case PANEL_TO_SHOW.SYMMETRY:
        return getSymmetryPanel();
      case PANEL_TO_SHOW.POSITION:
        return getPositionPanel();
      case PANEL_TO_SHOW.DEFAULT:
      default:
        return getDefaultPanels();
    }
  };

  const getDefaultPanels = (): ReactElement => {
    return <Panel panelHeader={undefined} panelContent={getContent()} />;
  };

  const onBack = (): void => {
    setPanelToShow(PANEL_TO_SHOW.DEFAULT);
  };

  return getPanel();
}
